import React, { Fragment, useContext, useState } from "react";
import propTypes from "prop-types";
import { Button, CircularProgress, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { FiAlertCircle, FiCheckCircle, FiDownload } from "react-icons/fi";

import { SessionContext } from "../../../../utility/useSession";
import { useDate } from "../../../../utility/useDate";
import { useTranslation } from "react-i18next";

enum ReportStatus {
    idle = 0,
    generating = 1,
    completed = 2,
    errored = 3
}

const ReportGenerator: React.FunctionComponent<{
    reportId: number;
    reportName: string;
    values?: Record<string, unknown> | null;
}> = (props) => {
    const session = useContext(SessionContext);
    const { localizeDate } = useDate();
    const [reportStatus, setReportStatus] = useState<ReportStatus>(ReportStatus.idle);
    const { t } = useTranslation();

    function downloadFile(data: BlobPart, filename: string, mime: string) {
        // It is necessary to create a new blob object with mime-type explicitly set
        // otherwise only Chrome works like it should
        const blob = new Blob([data], { type: mime || 'application/octet-stream' });
        // @ts-expect-error `msSaveBlob` does not exist on the `window.navigator` type.
        if (typeof window.navigator.msSaveBlob !== 'undefined') {
            // IE doesn't allow using a blob object directly as link href.
            // Workaround for "HTML7007: One or more blob URLs were
            // revoked by closing the blob for which they were created.
            // These URLs will no longer resolve as the data backing
            // the URL has been freed."
            // @ts-expect-error `msSaveBlob` does not exist on the `window.navigator` type.
            window.navigator.msSaveBlob(blob, filename);
            return;
        }
        // Other browsers
        // Create a link pointing to the ObjectURL containing the blob
        const blobURL = window.URL.createObjectURL(blob);
        const tempLink = document.createElement('a');
        tempLink.style.display = 'none';
        tempLink.href = blobURL;
        tempLink.setAttribute('download', filename);
        // Safari thinks _blank anchor are pop ups. We only want to set _blank
        // target if the browser does not support the HTML5 download attribute.
        // This allows you to download files in desktop safari if pop up blocking
        // is enabled.
        if (typeof tempLink.download === 'undefined') {
            tempLink.setAttribute('target', '_blank');
        }
        document.body.appendChild(tempLink);
        tempLink.click();
        document.body.removeChild(tempLink);
        setTimeout(() => {
            // For Firefox it is necessary to delay revoking the ObjectURL
            window.URL.revokeObjectURL(blobURL);
        }, 100);
    }

    const printReportPDF = () => {
        setReportStatus(ReportStatus.generating);
        const xhr = new XMLHttpRequest();

        xhr.open('POST', `${session.current.context.url}/reports/generateReportPDF`, true);
        xhr.setRequestHeader("content-type", "application/json");
        xhr.setRequestHeader("authorization", session.current.context.headers.authorization);
        xhr.responseType = "blob";
        xhr.onreadystatechange = function () {
            if (xhr.readyState == 4) {
                if (xhr.status != 200) {
                    setReportStatus(ReportStatus.errored);
                    setTimeout(() => {
                        setReportStatus(ReportStatus.idle);
                    }, 2000);
                    return;
                }
                const dt = localizeDate(new Date());
                const dtString = `${(dt.year - 2000).toString().padStart(2, "0")}${dt.month.toString().padStart(2, "0")}${dt.day.toString().padStart(2, "0")}`;
                const timeString = `${dt.hour.toString().padStart(2, "0")}${dt.minute.toString().padStart(2, "0")}${dt.second.toString().padStart(2, "0")}`;

                downloadFile(xhr.response, `${props.reportName}_${dtString}_${timeString}.pdf`, "application/pdf");
                setReportStatus(ReportStatus.completed);
                setTimeout(() => {
                    setReportStatus(ReportStatus.idle);
                }, 2000);
            }
        };
        xhr.send(JSON.stringify({
            reportId: props.reportId,
            values: props.values,
            timestamp: localizeDate(new Date()).toISO()
        }));
    }
    const printReportCSV = () => {
        setReportStatus(ReportStatus.generating);
        const xhr = new XMLHttpRequest();

        xhr.open('POST', `${session.current.context.url}/reports/generateReportCSV`, true);
        xhr.setRequestHeader("content-type", "application/json");
        xhr.setRequestHeader("authorization", session.current.context.headers.authorization);
        xhr.responseType = "blob";
        xhr.onreadystatechange = function () {
            if (xhr.readyState == 4) {
                if (xhr.status != 200) {
                    setReportStatus(ReportStatus.errored);
                    setTimeout(() => {
                        setReportStatus(ReportStatus.idle);
                    }, 2000);
                    return;
                }
                const dt = localizeDate(new Date());
                const dtString = `${(dt.year - 2000).toString().padStart(2, "0")}${dt.month.toString().padStart(2, "0")}${dt.day.toString().padStart(2, "0")}`;
                const timeString = `${dt.hour.toString().padStart(2, "0")}${dt.minute.toString().padStart(2, "0")}${dt.second.toString().padStart(2, "0")}`;

                downloadFile(xhr.response, `${props.reportName}_${dtString}_${timeString}.csv`, "application/csv");
                setReportStatus(ReportStatus.completed);
                setTimeout(() => {
                    setReportStatus(ReportStatus.idle);
                }, 2000);
            }
        };
        xhr.send(JSON.stringify({
            reportId: props.reportId,
            values: props.values,
            timestamp: localizeDate(new Date()).toISO()
        }));
    }

    return <Fragment>
        <Dialog
            open={reportStatus != ReportStatus.idle}
            keepMounted

            aria-labelledby="alert-dialog-slide-title"
            aria-describedby="alert-dialog-slide-description"
        >
            {reportStatus == ReportStatus.generating && (<Fragment><DialogTitle id="alert-dialog-slide-title">{t("reports.generatingReport")}</DialogTitle>
                <DialogContent>
                    <div style={{ textAlign: "center", paddingBottom: 20 }}>
                        <CircularProgress></CircularProgress>
                    </div>
                </DialogContent></Fragment>)}
            {reportStatus == ReportStatus.completed && (<Fragment><DialogTitle id="alert-dialog-slide-title">{t("reports.reportReady")}</DialogTitle>
                <DialogContent>
                    <div style={{ textAlign: "center", paddingBottom: 20 }}>
                        <FiCheckCircle size={40} color="green"></FiCheckCircle>
                    </div>
                </DialogContent></Fragment>)}
            {reportStatus == ReportStatus.errored && (<Fragment><DialogTitle id="alert-dialog-slide-title">{t("reports.errorGeneratingReport")}</DialogTitle>
                <DialogContent>
                    <div style={{ textAlign: "center", paddingBottom: 20 }}>
                        <FiAlertCircle size={40} color="red"></FiAlertCircle>
                    </div>
                </DialogContent></Fragment>)}
        </Dialog>
        <Button style={{ marginRight: 10 }} variant="contained" color="secondary" onClick={() => {
            printReportPDF();
        }}><FiDownload style={{ marginRight: 10 }} ></FiDownload>{t("reports.pdf")}</Button>
        <Button variant="contained" color="secondary" onClick={() => {
            printReportCSV();
        }}><FiDownload style={{ marginRight: 10 }} ></FiDownload>{t("reports.csv")}</Button>

    </Fragment>
}

ReportGenerator.propTypes = {
    reportId: propTypes.number.isRequired,
    reportName: propTypes.string.isRequired,
    values: propTypes.any,

}
export default ReportGenerator;